import {
  POLICY_APPLICATION_LABELS,
  POLICY_APPLICATIONS,
  SUPPRESSION_TYPES
} from '@kentik/ui-shared/alerting/constants';
import {
  APPLICATION_UMBRELLAS,
  getApplicationUmbrella,
  getTopLevelApplication,
  type ApplicationUmbrella
} from '@kentik/ui-shared/alerting/applicationUtils';

export type SuppressionApplication = ApplicationUmbrella | 'synthetics';

const SYNTHETICS_APPLICATION: SuppressionApplication = 'synthetics';

const SUPPRESSION_APPLICATIONS: SuppressionApplication[] = [
  ...Object.values(APPLICATION_UMBRELLAS),
  SYNTHETICS_APPLICATION
];

export const SUPPRESSION_APPLICATION_OPTIONS = SUPPRESSION_APPLICATIONS.map((application) => ({
  value: application,
  label: POLICY_APPLICATION_LABELS[application]
}));

const getSuppressionApplication = (application: string): SuppressionApplication | undefined =>
  getTopLevelApplication(application) === POLICY_APPLICATIONS.SYNTHETICS
    ? SYNTHETICS_APPLICATION
    : getApplicationUmbrella(application);

export const getSuppressionApplicationLabel = (application: SuppressionApplication): string =>
  POLICY_APPLICATION_LABELS[application];

export function deserializeSuppressionApplications(applications?: string[]): SuppressionApplication[] | undefined {
  if (applications === undefined) {
    return undefined;
  }

  return [...new Set(applications.map(getSuppressionApplication).filter((group) => group !== undefined))];
}

// Groups are a lossy view of stored values, so re-emit stored ones verbatim; API matches exactly.
export function serializeSuppressionApplications(
  applications: SuppressionApplication[] | undefined,
  stored: string[] = []
): string[] | undefined {
  if (applications === undefined) {
    return undefined;
  }

  const selected = new Set<string>(applications);
  const preserved = stored.filter((application) => {
    const group = getSuppressionApplication(application);

    return group === undefined || selected.has(group);
  });

  const covered = new Set(preserved.map(getSuppressionApplication).filter(Boolean));

  return [...preserved, ...applications.filter((group) => !covered.has(group))];
}

const APPLICATIONS_BY_ALERT_TYPE: Record<string, ApplicationUmbrella[]> = {
  [SUPPRESSION_TYPES.CONDITION]: [APPLICATION_UMBRELLAS.CORE, APPLICATION_UMBRELLAS.DDOS],
  [SUPPRESSION_TYPES.NMS]: [APPLICATION_UMBRELLAS.NMS],
  [SUPPRESSION_TYPES.METRIC]: [APPLICATION_UMBRELLAS.NMS]
};

const ALERT_TYPES_WITHOUT_APPLICATIONS: string[] = [SUPPRESSION_TYPES.SYNTH, SUPPRESSION_TYPES.AGENT];

export const supportsApplications = (alertType: string): boolean =>
  !ALERT_TYPES_WITHOUT_APPLICATIONS.includes(alertType);

export const getApplicationsForAlertType = (alertType: string): ApplicationUmbrella[] =>
  APPLICATIONS_BY_ALERT_TYPE[alertType] ?? [];

export const applicationsLockedByAlertType = ({
  alertType,
  hasKeyFilter
}: {
  alertType: string;
  hasKeyFilter: boolean;
}): boolean => hasKeyFilter && getApplicationsForAlertType(alertType).length > 0;
